// ============================================================
// PresenceLayer — ChartFlow
// ============================================================
// Shows who else is editing the chart in real time.
// - Floating avatar strip in the toolbar area
// - Bar-level highlight when a collaborator is editing a bar
// ============================================================

import React, { useEffect, useRef } from 'react';
import { useCollaborationStore } from '../../store/collaborationStore';

// ------------------------------------------------------------
// AVATAR STRIP
// Rendered inside the toolbar (passed as a component).
// Shows initials + color for each present collaborator.
// ------------------------------------------------------------

export function PresenceAvatars() {
  const { mode, presence } = useCollaborationStore();
  if (mode !== 'linked') return null;

  const users = Object.values(presence);
  if (users.length === 0) return null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: -4 }}>
      {users.map((p, i) => {
        const initials = p.email
          .split('@')[0]
          .slice(0, 2)
          .toUpperCase();
        return (
          <div
            key={p.userId}
            title={p.email}
            style={{
              width: 26, height: 26,
              borderRadius: '50%',
              background: p.color,
              border: '2px solid var(--color-paper)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700,
              color: 'white',
              marginLeft: i === 0 ? 0 : -6,
              zIndex: users.length - i,
              position: 'relative',
              cursor: 'default',
              transition: 'transform 150ms',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
          >
            {initials}
          </div>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------
// BAR HIGHLIGHT
// Wrap each BarCell with this to show who is editing it.
// Usage: <BarPresenceHighlight barId={bar.id}>...</BarPresenceHighlight>
// ------------------------------------------------------------

interface BarPresenceHighlightProps {
  barId:    string;
  children: React.ReactNode;
}

export function BarPresenceHighlight({ barId, children }: BarPresenceHighlightProps) {
  const { mode, presence } = useCollaborationStore();

  if (mode !== 'linked') return <>{children}</>;

  // Find any collaborator editing this bar
  const editor = Object.values(presence).find((p) => p.barId === barId);

  return (
    <div style={{ position: 'relative' }}>
      {children}
      {editor && (
        <>
          {/* Colored border highlight */}
          <div
            style={{
              position: 'absolute',
              inset: -2,
              borderRadius: 5,
              border: `2px solid ${editor.color}`,
              pointerEvents: 'none',
              zIndex: 10,
              transition: 'opacity 200ms',
            }}
          />
          {/* Name tag */}
          <div
            style={{
              position: 'absolute',
              top: -18,
              left: 0,
              background: editor.color,
              color: 'white',
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              padding: '1px 5px',
              borderRadius: '3px 3px 3px 0',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              zIndex: 11,
            }}
          >
            {editor.email.split('@')[0]}
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// PRESENCE STALE CLEANUP
// Removes collaborators who haven't sent a presence ping in 15s.
// Mount this once at the App level when in linked mode.
// ------------------------------------------------------------

export function PresenceStaleCleanup() {
  const { mode, presence, removePresence } = useCollaborationStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (mode !== 'linked') return;

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      Object.values(presence).forEach((p) => {
        if (now - p.lastSeen > 15_000) removePresence(p.userId);
      });
    }, 5_000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [mode, presence, removePresence]);

  return null;
}
